import React, { useState, useEffect, useCallback } from 'react';
import { useOutletContext } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { TopBar } from '../components/layout/TopBar';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '../components/ui/table';
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger
} from '../components/ui/tooltip';
import {
  BarChart3, Loader2, Users, DollarSign, TrendingUp, Calendar,
  FileText, Megaphone, MessageSquare, Activity, Eye, MousePointer,
  Clock, ChevronRight, Zap, ArrowUpRight, LayoutDashboard
} from 'lucide-react';
import {
  AreaChart, Area, BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip as ReTooltip, ResponsiveContainer, Legend
} from 'recharts';
import api, { interactionsAPI } from '../lib/api';
import { useEntitlements } from '../context/EntitlementsContext';
import { UpgradeGate } from '../components/UpgradeGate';

const COLORS = ['#056A84', '#00CCFF', '#1F9D7A', '#C58B2A', '#D64545', '#2B7DBA', '#8B5CF6'];

const ENTITY_LABELS = {
  module: 'Sección',
  event: 'Evento',
  announcement: 'Anuncio',
  post: 'Publicación',
  video: 'Video',
};

const ENTITY_ICONS = {
  module: LayoutDashboard,
  event: Calendar,
  announcement: Megaphone,
  post: FileText,
  video: FileText,
};

const ENTITY_COLORS = {
  module: '#056A84',
  event: '#1F9D7A',
  announcement: '#C58B2A',
  post: '#2B7DBA',
  video: '#8B5CF6',
};

function formatDuration(ms) {
  if (!ms || ms <= 0) return '0s';
  const secs = Math.round(ms / 1000);
  if (secs < 60) return `${secs}s`;
  const mins = Math.floor(secs / 60);
  const remainSecs = secs % 60;
  if (mins < 60) return `${mins}m ${remainSecs}s`;
  const hrs = Math.floor(mins / 60);
  return `${hrs}h ${mins % 60}m`;
}

function StatMini({ icon: Icon, label, value, color, sub }) {
  return (
    <div className="flex items-center gap-3">
      <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${color}15` }}>
        <Icon className="w-5 h-5" style={{ color }} />
      </div>
      <div>
        <p className="text-xl font-semibold text-foreground">{value}</p>
        <p className="text-[11px] text-muted-foreground">{label}</p>
        {sub && <p className="text-[10px] text-muted-foreground">{sub}</p>}
      </div>
    </div>
  );
}

function ChartCard({ title, children, className = '' }) {
  return (
    <Card className={`stat-card ${className}`}>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-semibold text-foreground">{title}</CardTitle>
      </CardHeader>
      <CardContent className="pt-0">{children}</CardContent>
    </Card>
  );
}

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-card border rounded-lg shadow-lg p-3 text-xs">
      <p className="font-medium mb-1">{label}</p>
      {payload.map((p, idx) => (
        <p key={p.name || p.dataKey || idx} style={{ color: p.color }}>
          {p.name}: <span className="font-semibold">{typeof p.value === 'number' && p.name.includes('$') ? `$${p.value.toFixed(2)}` : p.value}</span>
        </p>
      ))}
    </div>
  );
};

function SkeletonChart() {
  return (
    <div className="space-y-3">
      <Skeleton className="h-4 w-32" />
      <Skeleton className="h-[280px] w-full rounded-xl" />
    </div>
  );
}

// ============ INTERACTION DETAIL DIALOG ============
function InteractionDetailDialog({ open, onOpenChange, item, churchId, period }) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !item) return;
    setLoading(true);
    interactionsAPI.detail(item.entity_type, item.entity_id, churchId, period)
      .then(res => setDetail(res.data))
      .catch(() => setDetail(null))
      .finally(() => setLoading(false));
  }, [open, item, churchId, period]);

  const EntityIcon = item ? (ENTITY_ICONS[item.entity_type] || FileText) : FileText;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <EntityIcon className="w-5 h-5" style={{ color: ENTITY_COLORS[item?.entity_type] || '#056A84' }} />
            {item?.entity_title || 'Detalle'}
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-6 h-6 spinner text-[#056A84]" />
          </div>
        ) : detail ? (
          <div className="space-y-5">
            {/* KPIs */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <Card className="stat-card"><CardContent className="p-3">
                <StatMini icon={Eye} label="Vistas" value={detail.totals_by_type?.view?.count || 0} color="#056A84" />
              </CardContent></Card>
              <Card className="stat-card"><CardContent className="p-3">
                <StatMini icon={MousePointer} label="Clics" value={detail.totals_by_type?.click?.count || 0} color="#1F9D7A" />
              </CardContent></Card>
              <Card className="stat-card"><CardContent className="p-3">
                <StatMini icon={Users} label="Usuarios únicos" value={detail.total_unique_users || 0} color="#2B7DBA" />
              </CardContent></Card>
              <Card className="stat-card"><CardContent className="p-3">
                <StatMini icon={Clock} label="Tiempo prom." value={formatDuration(detail.totals_by_type?.time_spent?.avg_duration_ms || 0)} color="#C58B2A" />
              </CardContent></Card>
            </div>

            {/* Timeline Chart */}
            {detail.timeline && detail.timeline.length > 0 && (
              <ChartCard title="Interacciones por día">
                <div className="h-[220px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={detail.timeline}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                      <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                      <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                      <ReTooltip content={<CustomTooltip />} />
                      <Legend wrapperStyle={{ fontSize: '11px' }} />
                      <Bar dataKey="views" name="Vistas" fill="#056A84" radius={[3, 3, 0, 0]} />
                      <Bar dataKey="clicks" name="Clics" fill="#1F9D7A" radius={[3, 3, 0, 0]} />
                      <Bar dataKey="time_events" name="Tiempo" fill="#C58B2A" radius={[3, 3, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </ChartCard>
            )}
          </div>
        ) : (
          <div className="text-center py-8 text-muted-foreground">
            <BarChart3 className="w-10 h-10 mx-auto mb-2 opacity-30" />
            <p>No hay datos disponibles</p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ============ INTERACTIONS TAB ============
function InteractionsSection({ selectedChurchId, period }) {
  const [summary, setSummary] = useState(null);
  const [topContent, setTopContent] = useState([]);
  const [loading, setLoading] = useState(true);
  const [entityFilter, setEntityFilter] = useState('all');
  const [detailItem, setDetailItem] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);

  useEffect(() => {
    setLoading(true);
    const entityParam = entityFilter === 'all' ? undefined : entityFilter;
    Promise.all([
      interactionsAPI.summary(selectedChurchId, period, entityParam),
      interactionsAPI.topContent(selectedChurchId, entityParam, period, 20),
    ])
      .then(([summRes, topRes]) => {
        setSummary(summRes.data);
        setTopContent(topRes.data?.data || []);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [selectedChurchId, period, entityFilter]);

  const openDetail = (item) => {
    setDetailItem(item);
    setDetailOpen(true);
  };

  // Prepare pie data for entity type distribution
  const entityPieData = summary?.by_entity_type
    ? Object.entries(summary.by_entity_type).map(([type, data]) => ({
        name: ENTITY_LABELS[type] || type,
        value: data.total,
        color: ENTITY_COLORS[type] || '#056A84',
      }))
    : [];

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map(skeletonId => (
            <Card key={`skeleton-${skeletonId}`} className="stat-card"><CardContent className="p-4"><Skeleton className="h-16 w-full" /></CardContent></Card>
          ))}
        </div>
        <SkeletonChart />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Filter */}
      <div className="flex items-center gap-3">
        <Select value={entityFilter} onValueChange={setEntityFilter}>
          <SelectTrigger className="w-[180px] h-9" data-testid="interactions-entity-filter">
            <SelectValue placeholder="Filtrar por tipo" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todo el contenido</SelectItem>
            <SelectItem value="module">Secciones de la Suite</SelectItem>
            <SelectItem value="event">Eventos</SelectItem>
            <SelectItem value="announcement">Anuncios</SelectItem>
            <SelectItem value="post">Publicaciones</SelectItem>
            <SelectItem value="video">Videos</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* KPI Row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="stat-card" data-testid="interactions-kpi-total">
          <CardContent className="p-4">
            <StatMini icon={Zap} label="Total interacciones" value={summary?.total_interactions || 0} color="#056A84" />
          </CardContent>
        </Card>
        <Card className="stat-card" data-testid="interactions-kpi-views">
          <CardContent className="p-4">
            <StatMini icon={Eye} label="Vistas" value={summary?.totals_by_type?.view?.count || 0} color="#1F9D7A" />
          </CardContent>
        </Card>
        <Card className="stat-card" data-testid="interactions-kpi-clicks">
          <CardContent className="p-4">
            <StatMini icon={MousePointer} label="Clics" value={summary?.totals_by_type?.click?.count || 0} color="#C58B2A" />
          </CardContent>
        </Card>
        <Card className="stat-card" data-testid="interactions-kpi-users">
          <CardContent className="p-4">
            <StatMini icon={Users} label="Usuarios únicos" value={summary?.total_unique_users || 0} color="#2B7DBA"
              sub={summary?.totals_by_type?.time_spent?.count ? `${formatDuration(summary.totals_by_type.time_spent.avg_duration_ms)} prom.` : undefined}
            />
          </CardContent>
        </Card>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Timeline */}
        <ChartCard title="Interacciones en el tiempo">
          {summary?.timeline && summary.timeline.length > 0 ? (
            <div className="h-[280px]">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={summary.timeline}>
                  <defs>
                    <linearGradient id="gradViews" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#056A84" stopOpacity={0.3} />
                      <stop offset="100%" stopColor="#056A84" stopOpacity={0.02} />
                    </linearGradient>
                    <linearGradient id="gradClicks" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#1F9D7A" stopOpacity={0.3} />
                      <stop offset="100%" stopColor="#1F9D7A" stopOpacity={0.02} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                  <ReTooltip content={<CustomTooltip />} />
                  <Legend wrapperStyle={{ fontSize: '11px' }} />
                  <Area type="monotone" dataKey="views" name="Vistas" stroke="#056A84" fill="url(#gradViews)" strokeWidth={2} />
                  <Area type="monotone" dataKey="clicks" name="Clics" stroke="#1F9D7A" fill="url(#gradClicks)" strokeWidth={2} />
                  <Line type="monotone" dataKey="time_events" name="Tiempo" stroke="#C58B2A" strokeWidth={2} dot={{ r: 3 }} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="h-[280px] flex items-center justify-center text-muted-foreground">
              <div className="text-center">
                <BarChart3 className="w-10 h-10 mx-auto mb-2 opacity-30" />
                <p className="text-sm">Sin datos de interacciones aún</p>
                <p className="text-xs mt-1">Las interacciones se registran desde la app móvil</p>
              </div>
            </div>
          )}
        </ChartCard>

        {/* Entity Type Distribution */}
        <ChartCard title="Distribución por tipo de contenido">
          {entityPieData.length > 0 ? (
            <div className="h-[280px] flex items-center justify-center">
              <div className="w-1/2">
                <ResponsiveContainer width="100%" height={220}>
                  <PieChart>
                    <Pie
                      data={entityPieData}
                      cx="50%"
                      cy="50%"
                      innerRadius={55}
                      outerRadius={85}
                      dataKey="value"
                      label={({ name, value }) => `${value}`}
                      labelLine={false}
                    >
                      {entityPieData.map((entry, i) => (
                        <Cell key={entry.name} fill={entry.color} />
                      ))}
                    </Pie>
                    <ReTooltip />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="w-1/2 space-y-3">
                {entityPieData.map((entry) => (
                  <div key={entry.name} className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full shrink-0" style={{ backgroundColor: entry.color }} />
                    <span className="text-sm text-foreground">{entry.name}</span>
                    <span className="text-sm font-semibold ml-auto">{entry.value}</span>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="h-[280px] flex items-center justify-center text-muted-foreground">
              <p className="text-sm">Sin datos</p>
            </div>
          )}
        </ChartCard>
      </div>

      {/* Top Content Table */}
      <ChartCard title="Contenido más interactuado">
        {topContent.length > 0 ? (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[40%]">Contenido</TableHead>
                  <TableHead className="text-center">Vistas</TableHead>
                  <TableHead className="text-center">Clics</TableHead>
                  <TableHead className="text-center">Usuarios</TableHead>
                  <TableHead className="text-center">Tiempo prom.</TableHead>
                  <TableHead className="text-right">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {topContent.map((item, idx) => {
                  const EntityIcon = ENTITY_ICONS[item.entity_type] || FileText;
                  return (
                    <TableRow key={`${item.entity_type}-${item.entity_id}`} className="group" data-testid={`top-content-row-${idx}`}>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <div
                            className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0"
                            style={{ backgroundColor: `${ENTITY_COLORS[item.entity_type] || '#056A84'}15` }}
                          >
                            <EntityIcon className="w-4 h-4" style={{ color: ENTITY_COLORS[item.entity_type] || '#056A84' }} />
                          </div>
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-foreground truncate">{item.entity_title}</p>
                            <Badge variant="secondary" className="text-[10px] px-1.5 py-0 mt-0.5">
                              {ENTITY_LABELS[item.entity_type] || item.entity_type}
                            </Badge>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="text-center">
                        <div className="flex items-center justify-center gap-1">
                          <Eye className="w-3 h-3 text-muted-foreground" />
                          <span className="text-sm font-medium">{item.views}</span>
                        </div>
                      </TableCell>
                      <TableCell className="text-center">
                        <div className="flex items-center justify-center gap-1">
                          <MousePointer className="w-3 h-3 text-muted-foreground" />
                          <span className="text-sm font-medium">{item.clicks}</span>
                        </div>
                      </TableCell>
                      <TableCell className="text-center">
                        <div className="flex items-center justify-center gap-1">
                          <Users className="w-3 h-3 text-muted-foreground" />
                          <span className="text-sm font-medium">{item.unique_users}</span>
                        </div>
                      </TableCell>
                      <TableCell className="text-center">
                        <span className="text-sm text-muted-foreground">{formatDuration(item.avg_duration_ms)}</span>
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openDetail(item)}
                          className="opacity-0 group-hover:opacity-100 transition-opacity"
                          data-testid={`detail-btn-${item.entity_type}-${item.entity_id}`}
                        >
                          <ChevronRight className="w-4 h-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        ) : (
          <div className="text-center py-8 text-muted-foreground">
            <Activity className="w-10 h-10 mx-auto mb-2 opacity-30" />
            <p className="text-sm">No hay interacciones registradas aún</p>
            <p className="text-xs mt-1">Las interacciones se registran cuando los usuarios de la app móvil ven, hacen clic o interactúan con el contenido</p>
          </div>
        )}
      </ChartCard>

      {/* Detail Dialog */}
      <InteractionDetailDialog
        open={detailOpen}
        onOpenChange={setDetailOpen}
        item={detailItem}
        churchId={selectedChurchId}
        period={period}
      />
    </div>
  );
}

// ============ MAIN ANALYTICS PAGE ============
export default function AnalyticsPage() {
  const { selectedChurchId, canRead } = useAuth();
  const { setMobileMenuOpen, churchInfo } = useOutletContext();
  const [period, setPeriod] = useState('month');
  const [activeTab, setActiveTab] = useState('general');
  const [loading, setLoading] = useState(true);
  const [members, setMembers] = useState(null);
  const [contributions, setContributions] = useState(null);
  const [engagement, setEngagement] = useState(null);
  const { hasModule, loading: entitlementsLoading } = useEntitlements();
  const analyticsEnabled = hasModule('analiticas_avanzadas');

  useEffect(() => {
    if (activeTab !== 'general' || !analyticsEnabled) return;
    const load = async () => {
      setLoading(true);
      try {
        const params = { params: { church_id: selectedChurchId, period } };
        const [mRes, eRes] = await Promise.all([
          api.get('/api/analytics/members', params),
          api.get('/api/analytics/engagement', params),
        ]);
        setMembers(mRes.data);
        setEngagement(eRes.data);

        if (canRead('contributions')) {
          const cRes = await api.get('/api/analytics/contributions', params);
          setContributions(cRes.data);
        }
      } catch {
        // Error handled silently
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [selectedChurchId, period, activeTab, canRead, analyticsEnabled]);

  // Prepare pie data - extract nested ternaries to helper
  const translateStatus = (name) => {
    if (name === 'approved') return 'Aprobados';
    if (name === 'pending') return 'Pendientes';
    if (name === 'rejected') return 'Rechazados';
    return name;
  };

  const statusPieData = members ? Object.entries(members.status_distribution).map(([name, value]) => ({
    name: translateStatus(name),
    value
  })) : [];

  const rolePieData = members ? Object.entries(members.role_distribution).map(([name, value]) => ({ name, value })) : [];

  const conceptData = contributions ? Object.entries(contributions.concept_distribution).map(([name, v]) => ({
    name, count: v.count, amount: v.amount
  })) : [];

  if (!entitlementsLoading && !analyticsEnabled) {
    return (
      <div>
        <TopBar title="Analíticas" onMenuClick={() => setMobileMenuOpen(true)} churchInfo={churchInfo} />
        <UpgradeGate
          title="Analíticas avanzadas"
          description="Los reportes de crecimiento, aportes e interacciones son un módulo adicional."
          benefits={[
            'Crecimiento de miembros y actividad por período',
            'Reportes de aportes por concepto y estado',
            'Interacciones de la congregación con el contenido publicado',
          ]}
        />
      </div>
    );
  }

  return (
    <div>
      <TopBar title="Analíticas" onMenuClick={() => setMobileMenuOpen(true)} churchInfo={churchInfo} />
      <div className="px-4 sm:px-6 lg:px-8 py-5">
        {/* Header with tabs and period selector */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-5">
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList data-testid="analytics-main-tabs">
              <TabsTrigger value="general" data-testid="analytics-tab-general">
                <BarChart3 className="w-4 h-4 mr-1.5" />
                Resumen
              </TabsTrigger>
              <TabsTrigger value="interactions" data-testid="analytics-tab-interactions">
                <Activity className="w-4 h-4 mr-1.5" />
                Interacciones
              </TabsTrigger>
            </TabsList>
          </Tabs>
          <Tabs value={period} onValueChange={setPeriod}>
            <TabsList>
              <TabsTrigger value="week" data-testid="analytics-period-week">Semanal</TabsTrigger>
              <TabsTrigger value="month" data-testid="analytics-period-month">Mensual</TabsTrigger>
            </TabsList>
          </Tabs>
        </div>

        {/* General Analytics Tab */}
        {activeTab === 'general' && (
          <>
            {loading ? (
              <div className="flex items-center justify-center py-20">
                <Loader2 className="w-8 h-8 spinner text-[#056A84]" />
              </div>
            ) : (
              <div className="space-y-6">
                {/* KPI Row */}
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                  <Card className="stat-card"><CardContent className="p-4">
                    <StatMini icon={Users} label="Miembros" value={members?.total || 0} color="#056A84" sub={`${members?.activity?.active_30d || 0} activos (30d)`} />
                  </CardContent></Card>
                  <Card className="stat-card"><CardContent className="p-4">
                    <StatMini icon={Activity} label="Activos 7d" value={members?.activity?.active_7d || 0} color="#1F9D7A" />
                  </CardContent></Card>
                  {contributions && (
                    <Card className="stat-card"><CardContent className="p-4">
                      <StatMini icon={DollarSign} label="Total recaudado" value={`Bs. ${Number(contributions?.total_amount || 0).toLocaleString('es-VE', { maximumFractionDigits: 0 })}`} color="#C58B2A" sub={`${contributions?.total_count || 0} registros`} />
                    </CardContent></Card>
                  )}
                  <Card className="stat-card"><CardContent className="p-4">
                    <StatMini icon={Calendar} label="Eventos" value={engagement?.totals?.events || 0} color="#1F9D7A" />
                  </CardContent></Card>
                  <Card className="stat-card"><CardContent className="p-4">
                    <StatMini icon={FileText} label="Publicaciones" value={engagement?.totals?.posts || 0} color="#2B7DBA" />
                  </CardContent></Card>
                  <Card className="stat-card"><CardContent className="p-4">
                    <StatMini icon={MessageSquare} label="Peticiones" value={engagement?.petitions_stats?.total || 0} color="#D64545" sub={`${engagement?.petitions_stats?.answered || 0} respondidas`} />
                  </CardContent></Card>
                </div>

                {/* Charts Row 1: Member Growth + Activity */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <ChartCard title={`Crecimiento de miembros (${period === 'week' ? 'semanal' : 'mensual'})`}>
                    <div className="h-[280px]">
                      <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={members?.growth || []}>
                          <defs>
                            <linearGradient id="gradMembers" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="0%" stopColor="#056A84" stopOpacity={0.3} />
                              <stop offset="100%" stopColor="#056A84" stopOpacity={0.02} />
                            </linearGradient>
                          </defs>
                          <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                          <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                          <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                          <ReTooltip content={<CustomTooltip />} />
                          <Area type="monotone" dataKey="count" name="Nuevos miembros" stroke="#056A84" fill="url(#gradMembers)" strokeWidth={2} />
                        </AreaChart>
                      </ResponsiveContainer>
                    </div>
                  </ChartCard>

                  <ChartCard title="Distribución de miembros">
                    <div className="h-[280px] flex items-center">
                      <div className="w-1/2">
                        <ResponsiveContainer width="100%" height={200}>
                          <PieChart>
                            <Pie data={statusPieData} cx="50%" cy="50%" innerRadius={50} outerRadius={80} dataKey="value" label={({ name, value }) => `${name}: ${value}`} labelLine={false}>
                              {statusPieData.map((entry, i) => <Cell key={`status-${entry.name}`} fill={COLORS[i % COLORS.length]} />)}
                            </Pie>
                            <ReTooltip />
                          </PieChart>
                        </ResponsiveContainer>
                        <p className="text-center text-xs text-muted-foreground">Por estado</p>
                      </div>
                      <div className="w-1/2">
                        <ResponsiveContainer width="100%" height={200}>
                          <PieChart>
                            <Pie data={rolePieData} cx="50%" cy="50%" innerRadius={50} outerRadius={80} dataKey="value" label={({ name, value }) => `${value}`} labelLine={false}>
                              {rolePieData.map((entry, i) => <Cell key={`role-${entry.name}`} fill={COLORS[(i + 2) % COLORS.length]} />)}
                            </Pie>
                            <ReTooltip />
                          </PieChart>
                        </ResponsiveContainer>
                        <p className="text-center text-xs text-muted-foreground">Por rol</p>
                      </div>
                    </div>
                  </ChartCard>
                </div>

                {/* Charts Row 2: Contributions + Engagement */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {contributions && (
                    <ChartCard title={`Contribuciones (${period === 'week' ? 'semanal' : 'mensual'})`}>
                      <div className="h-[280px]">
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart data={contributions?.timeline || []}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                            <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                            <YAxis tick={{ fontSize: 11 }} />
                            <ReTooltip content={<CustomTooltip />} />
                            <Bar dataKey="amount" name="$ Monto" fill="#056A84" radius={[4, 4, 0, 0]} />
                            <Bar dataKey="count" name="Cantidad" fill="#00CCFF" radius={[4, 4, 0, 0]} />
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                    </ChartCard>
                  )}

                  <ChartCard title={`Actividad de contenido (${period === 'week' ? 'semanal' : 'mensual'})`}>
                    <div className="h-[280px]">
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={engagement?.timeline || []}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                          <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                          <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                          <ReTooltip content={<CustomTooltip />} />
                          <Legend wrapperStyle={{ fontSize: '11px' }} />
                          <Line type="monotone" dataKey="eventos" stroke="#1F9D7A" strokeWidth={2} dot={{ r: 3 }} />
                          <Line type="monotone" dataKey="publicaciones" stroke="#2B7DBA" strokeWidth={2} dot={{ r: 3 }} />
                          <Line type="monotone" dataKey="anuncios" stroke="#C58B2A" strokeWidth={2} dot={{ r: 3 }} />
                          <Line type="monotone" dataKey="peticiones" stroke="#D64545" strokeWidth={2} dot={{ r: 3 }} />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  </ChartCard>
                </div>

                {/* Row 3: Concepts + Petitions */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {contributions && conceptData.length > 0 && (
                    <ChartCard title="Contribuciones por concepto">
                      <div className="h-[250px]">
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart data={conceptData} layout="vertical">
                            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                            <XAxis type="number" tick={{ fontSize: 11 }} />
                            <YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} width={100} />
                            <ReTooltip content={<CustomTooltip />} />
                            <Bar dataKey="amount" name="$ Monto" fill="#056A84" radius={[0, 4, 4, 0]} />
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                    </ChartCard>
                  )}

                  {engagement && (
                    <ChartCard title="Estado de peticiones">
                      <div className="h-[250px] flex items-center justify-center">
                        <div className="grid grid-cols-2 gap-6 text-center">
                          <div>
                            <p className="text-4xl font-bold text-[#056A84]">{engagement.petitions_stats?.answered || 0}</p>
                            <p className="text-sm text-muted-foreground">Respondidas</p>
                          </div>
                          <div>
                            <p className="text-4xl font-bold text-[#C58B2A]">{engagement.petitions_stats?.pending || 0}</p>
                            <p className="text-sm text-muted-foreground">Pendientes</p>
                          </div>
                          <div>
                            <p className="text-4xl font-bold text-[#1F9D7A]">{engagement.petitions_stats?.active || 0}</p>
                            <p className="text-sm text-muted-foreground">Activas</p>
                          </div>
                          <div>
                            <p className="text-4xl font-bold text-foreground">{engagement.petitions_stats?.total || 0}</p>
                            <p className="text-sm text-muted-foreground">Total</p>
                          </div>
                        </div>
                      </div>
                    </ChartCard>
                  )}
                </div>

                {/* Activity Summary */}
                {members && (
                  <ChartCard title="Actividad de conexión de miembros">
                    <div className="grid grid-cols-3 gap-6 py-4">
                      <div className="text-center">
                        <div className="w-20 h-20 mx-auto rounded-full bg-[#1F9D7A]/10 flex items-center justify-center mb-2">
                          <p className="text-2xl font-bold text-[#1F9D7A]">{members.activity?.active_7d || 0}</p>
                        </div>
                        <p className="text-sm font-medium">Activos 7 días</p>
                        <p className="text-[10px] text-muted-foreground">Se conectaron esta semana</p>
                      </div>
                      <div className="text-center">
                        <div className="w-20 h-20 mx-auto rounded-full bg-[#2B7DBA]/10 flex items-center justify-center mb-2">
                          <p className="text-2xl font-bold text-[#2B7DBA]">{members.activity?.active_30d || 0}</p>
                        </div>
                        <p className="text-sm font-medium">Activos 30 días</p>
                        <p className="text-[10px] text-muted-foreground">Se conectaron este mes</p>
                      </div>
                      <div className="text-center">
                        <div className="w-20 h-20 mx-auto rounded-full bg-[#3B5B66]/10 flex items-center justify-center mb-2">
                          <p className="text-2xl font-bold text-[#3B5B66]">{members.activity?.inactive || 0}</p>
                        </div>
                        <p className="text-sm font-medium">Inactivos</p>
                        <p className="text-[10px] text-muted-foreground">Más de 30 días sin conexión</p>
                      </div>
                    </div>
                  </ChartCard>
                )}
              </div>
            )}
          </>
        )}

        {/* Interactions Tab */}
        {activeTab === 'interactions' && (
          <InteractionsSection
            selectedChurchId={selectedChurchId}
            period={period}
          />
        )}
      </div>
    </div>
  );
}
